// Job Description Text Parser Utilities
// Extracts structured data from raw job description text

/**
 * Extract text between two markers (flexible matching)
 */
export function extractBetween(text, start, end) {
  const s = text.indexOf(start);
  const e = text.indexOf(end);
  if (s === -1) return '';
  if (e === -1) return text.substring(s + start.length);
  return text.substring(s + start.length, e).trim();
}

/**
 * Extract text between markers with regex support
 */
export function extractBetweenRegex(text, startPattern, endPattern) {
  const startMatch = text.match(startPattern);
  if (!startMatch) return '';
  const startIdx = startMatch.index + startMatch[0].length;
  const remaining = text.substring(startIdx);
  const endMatch = remaining.match(endPattern);
  if (!endMatch) return remaining.trim();
  return remaining.substring(0, endMatch.index).trim();
}

export function extract(text, startMarker, endMarker) {
  return extractBetween(text, startMarker, endMarker)
    .replace(/\n/g, ' ')
    .trim();
}

/**
 * Split text into bullet points (handles •, -, *, numbered lists)
 */
export function splitIntoBullets(text) {
  if (!text) return [];
  return text
    .split(/[•\-*]|\d+\.\s/)
    .map(s => s.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim())
    .filter(s => s.length > 5);
}

/**
 * Parse responsibilities section with flexible matching
 */
export function parseResponsibilities(text) {
  // Try to find section D content
  const sectionPatterns = [
    /D\.\s*MAIN\s*OBJECTIVES.*?KEY\s*RESPONSIBILITIES/i,
    /D\.\s*KEY\s*RESPONSIBILITIES/i,
    /MAIN\s*OBJECTIVES.*?RESPONSIBILITIES/i,
    /KEY\s*RESPONSIBILITIES/i,
  ];
  
  let sectionText = '';
  for (const pattern of sectionPatterns) {
    const match = text.match(pattern);
    if (match) {
      const startIdx = match.index + match[0].length;
      const endPatterns = [/E\.\s*OUTPUTS/i, /OUTPUTS\s*PROFILE/i, /F\.\s*CORE/i];
      for (const endPattern of endPatterns) {
        const endMatch = text.substring(startIdx).match(endPattern);
        if (endMatch) {
          sectionText = text.substring(startIdx, startIdx + endMatch.index);
          break;
        }
      }
      if (sectionText) break;
      sectionText = text.substring(startIdx, startIdx + 3000); // Take up to 3000 chars
      break;
    }
  }

  if (!sectionText) return [];

  // Parse numbered headings and their activities
  const headingPattern = /(\d+\.?\s*[A-Za-z][^•\n]{10,80})/g;
  const matches = [...sectionText.matchAll(headingPattern)];
  
  if (matches.length === 0) {
    // Fallback: return as single responsibility
    const bullets = splitIntoBullets(sectionText);
    return bullets.length > 0 ? [{ heading: 'Key Responsibilities', activities: bullets }] : [];
  }

  return matches.map((match, i) => {
    const heading = match[1].replace(/^\d+\.?\s*/, '').trim();
    const startIdx = match.index + match[0].length;
    const endIdx = i < matches.length - 1 ? matches[i + 1].index : sectionText.length;
    const content = sectionText.substring(startIdx, endIdx);
    const activities = splitIntoBullets(content);
    return { heading, activities };
  }).filter(r => r.activities.length > 0);
}

/**
 * Parse outputs profile section with flexible matching  
 */
export function parseOutputsProfile(text) {
  const sectionPatterns = [
    /E\.\s*OUTPUTS\s*PROFILE/i,
    /OUTPUTS\s*PROFILE.*?STAKEHOLDER/i,
  ];
  
  let sectionText = '';
  for (const pattern of sectionPatterns) {
    const match = text.match(pattern);
    if (match) {
      const startIdx = match.index + match[0].length;
      const endPatterns = [/F\.\s*CORE/i, /CORE\s*COMPETENCIES/i, /G\.\s*CAREER/i];
      for (const endPattern of endPatterns) {
        const endMatch = text.substring(startIdx).match(endPattern);
        if (endMatch) {
          sectionText = text.substring(startIdx, startIdx + endMatch.index);
          break;
        }
      }
      if (sectionText) break;
      sectionText = text.substring(startIdx, startIdx + 2000);
      break;
    }
  }

  if (!sectionText) return [];

  // Look for customer/stakeholder patterns
  const customerPatterns = [
    { customer: 'Supervisor/Manager', pattern: /supervisor.*?manager/i },
    { customer: 'Head of Branch/Division', pattern: /head\s*of\s*branch/i },
    { customer: 'Other Departments', pattern: /other\s*departments/i },
    { customer: 'External Stakeholders', pattern: /external\s*stakeholders/i },
    { customer: 'Managers of the Department', pattern: /managers\s*of\s*the\s*department/i },
    { customer: 'Staff in the Department', pattern: /staff\s*in\s*the\s*department/i },
    { customer: 'Subordinate Employees', pattern: /subordinate\s*employees/i },
  ];

  const results = [];
  for (let i = 0; i < customerPatterns.length; i++) {
    const cp = customerPatterns[i];
    const match = sectionText.match(cp.pattern);
    if (match) {
      const startIdx = match.index + match[0].length;
      let endIdx = sectionText.length;
      // Find next customer
      for (let j = i + 1; j < customerPatterns.length; j++) {
        const nextMatch = sectionText.substring(startIdx).match(customerPatterns[j].pattern);
        if (nextMatch) {
          endIdx = startIdx + nextMatch.index;
          break;
        }
      }
      const content = sectionText.substring(startIdx, endIdx);
      const expectations = splitIntoBullets(content);
      if (expectations.length > 0) {
        results.push({ customer: cp.customer, expectations });
      }
    }
  }
  
  return results;
}

/**
 * Parse competencies section with flexible matching
 */
export function parseCompetencies(text) {
  const sectionPatterns = [
    /F\.\s*CORE\s*COMPETENCIES/i,
    /CORE\s*COMPETENCIES/i,
  ];
  
  let sectionText = '';
  for (const pattern of sectionPatterns) {
    const match = text.match(pattern);
    if (match) {
      const startIdx = match.index + match[0].length;
      const endPatterns = [/G\.\s*CAREER/i, /CAREER\s*PATHING/i, /H\.\s*AMENDMENTS/i];
      for (const endPattern of endPatterns) {
        const endMatch = text.substring(startIdx).match(endPattern);
        if (endMatch) {
          sectionText = text.substring(startIdx, startIdx + endMatch.index);
          break;
        }
      }
      if (sectionText) break;
      sectionText = text.substring(startIdx, startIdx + 2000);
      break;
    }
  }

  const extractField = (patternStart, patternEnd) => {
    const startMatch = sectionText.match(patternStart);
    if (!startMatch) return '';
    const startIdx = startMatch.index + startMatch[0].length;
    const remaining = sectionText.substring(startIdx);
    const endMatch = remaining.match(patternEnd);
    if (endMatch) {
      return remaining.substring(0, endMatch.index).replace(/[•\-*]/g, '').replace(/\s+/g, ' ').trim();
    }
    return remaining.substring(0, 200).replace(/[•\-*]/g, '').replace(/\s+/g, ' ').trim();
  };

  const extractListField = (patternStart, patternEnd) => {
    const startMatch = sectionText.match(patternStart);
    if (!startMatch) return [];
    const startIdx = startMatch.index + startMatch[0].length;
    const remaining = sectionText.substring(startIdx);
    const endMatch = remaining.match(patternEnd);
    const content = endMatch ? remaining.substring(0, endMatch.index) : remaining.substring(0, 500);
    return splitIntoBullets(content);
  };

  return {
    minimum_qualification: extractField(/minimum\s*qualification/i, /professional|experience|knowledge/i),
    professional_requirements: null,
    minimum_experience: extractField(/minimum\s*experience/i, /knowledge|skills/i),
    knowledge: extractListField(/knowledge/i, /skills|personal/i),
    skills: extractListField(/skills/i, /personal\s*attributes|career/i),
    personal_attributes: extractListField(/personal\s*attributes/i, /career|g\./i),
  };
}

/**
 * Extract section text using flexible regex patterns
 */
function extractSectionText(text, startPatterns, endPatterns, maxLength = 2000) {
  for (const startPattern of startPatterns) {
    const match = text.match(startPattern);
    if (match) {
      const startIdx = match.index + match[0].length;
      for (const endPattern of endPatterns) {
        const endMatch = text.substring(startIdx).match(endPattern);
        if (endMatch) {
          return text.substring(startIdx, startIdx + endMatch.index).trim();
        }
      }
      return text.substring(startIdx, startIdx + maxLength).trim();
    }
  }
  return '';
}

/**
 * Main parser function - extracts structured data from raw job description text
 */
export function parseJobDescriptionText(rawText) {
  // 1. Clean noise but preserve structure
  let text = rawText
    .replace(/Page\s*\d+\s*of\s*\d+/gi, '')
    .trim();

  // 2. Extract META (Section A) - flexible patterns
  const meta = {
    post_title: extractMetaField(text, /post\s*title/i, /core\s*code|occupational/i) || 
                extractMetaField(text, /job\s*title/i, /core|department|location/i),
    core_code: extractMetaField(text, /core\s*code/i, /occupational|classification/i),
    occupational_classification: extractMetaField(text, /occupational\s*classification/i, /salary|level|post/i),
    post_salary_level: extractMetaField(text, /post.*?salary.*?level|salary\s*level/i, /location|branch/i),
    location: extractMetaField(text, /location\s*(of\s*post)?/i, /branch|component|reports/i),
    branch_component: extractMetaField(text, /branch.*?component/i, /reports|supervisor/i),
    reports_to: extractMetaField(text, /post\s*reports\s*to|reports\s*to/i, /incumbent|page|section/i),
    incumbent_name: null,
  };

  // 3. Extract JOB PURPOSE (Section B)
  const job_purpose = extractSectionText(
    text,
    [/B\.\s*JOB\s*PURPOSE/i, /JOB\s*PURPOSE/i],
    [/C\.\s*MAIN\s*FUNCTIONS/i, /MAIN\s*FUNCTIONS/i, /D\.\s*MAIN/i]
  );

  // 4. Extract MAIN FUNCTIONS (Section C)
  const functionsText = extractSectionText(
    text,
    [/C\.\s*MAIN\s*FUNCTIONS/i, /MAIN\s*FUNCTIONS\s*OF\s*THE\s*POST/i],
    [/D\.\s*MAIN\s*OBJECTIVES/i, /KEY\s*RESPONSIBILITIES/i, /E\.\s*OUTPUTS/i]
  );
  const main_functions = functionsText
    .split(/\d+\./)
    .map(s => s.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim())
    .filter(s => s.length > 5);

  // 5. Extract RESPONSIBILITIES (Section D)
  const responsibilities = parseResponsibilities(text);

  // 6. Extract OUTPUTS PROFILE (Section E)
  const outputs_profile = parseOutputsProfile(text);

  // 7. Extract COMPETENCIES (Section F)
  const competencies = parseCompetencies(text);

  // 8. Extract NOTICES (Sections G-J)
  const career_pathing = extractSectionText(
    text,
    [/G\.\s*CAREER\s*PATHING/i, /CAREER\s*PATHING/i],
    [/H\.\s*AMENDMENTS/i, /AMENDMENTS/i, /I\.\s*PERFORMANCE/i]
  );
  
  const amendments = extractSectionText(
    text,
    [/H\.\s*AMENDMENTS/i, /AMENDMENTS\s*TO\s*JOB/i],
    [/I\.\s*PERFORMANCE/i, /PERFORMANCE\s*AGREEMENT/i, /J\.\s*JOB/i]
  );
  
  const performance_agreement = extractSectionText(
    text,
    [/I\.\s*PERFORMANCE\s*AGREEMENT/i, /PERFORMANCE\s*AGREEMENT/i],
    [/J\.\s*JOB\s*DESCRIPTION/i, /SIGNATORIES/i, /SIGNATURE/i]
  );

  // 9. Signatories (Section J)
  const signatories = [
    { role: 'CD: Corporate Management', name: null, signature: null, date: null },
    { role: 'Supervisor/Manager', name: null, signature: null, date: null },
    { role: 'Incumbent', name: null, signature: null, date: null },
  ];

  // 10. Organogram (extract if present)
  const organogram = {
    top: null,
    peers: [],
    current_role: null,
  };

  return {
    meta,
    organogram,
    job_purpose,
    main_functions,
    responsibilities,
    outputs_profile,
    competencies,
    career_pathing,
    amendments,
    performance_agreement,
    signatories,
  };
}

/**
 * Helper to extract meta field values
 */
function extractMetaField(text, startPattern, endPattern) {
  const match = text.match(startPattern);
  if (!match) return null;
  const startIdx = match.index + match[0].length;
  const remaining = text.substring(startIdx, startIdx + 300);
  const endMatch = remaining.match(endPattern);
  const value = endMatch ? remaining.substring(0, endMatch.index) : remaining.substring(0, 100);
  return value.replace(/[:\n]/g, ' ').replace(/\s+/g, ' ').trim() || null;
}
